import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OurAuthService } from 'app/core/auth/auth.service';
import { ReportService } from '../../reports.service';
import { ViewReportComponent } from '../view-report/view-report.component';
import { ViewNewReportComponent } from '../view-new-report/view-new-report.component';

export type ReportViewMode = 'legacy' | 'html';

@Component({
    selector: 'app-view-report-shell',
    standalone: true,
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        ViewReportComponent,
        ViewNewReportComponent,
    ],
    templateUrl: './view-report-shell.component.html',
    styleUrls: ['./view-report-shell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewReportShellComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private reportService = inject(ReportService);
    private authService = inject(OurAuthService);
    private cdr = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);

    resolving = true;
    view: ReportViewMode | null = null;

    ngOnInit(): void {
        this.route.paramMap
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((params) => {
                    const reportId = params.get('reportId') ?? '';
                    this.resolving = true;
                    this.view = null;
                    this.cdr.markForCheck();

                    if (!reportId) {
                        return of('');
                    }

                    return this.reportService.getReportHtml(reportId);
                })
            )
            .subscribe({
                next: (body) => {
                    this.view = this.resolveViewMode(body ?? '');
                    this.resolving = false;
                    this.cdr.markForCheck();
                },
                error: (err: unknown) => {
                    if (err instanceof HttpErrorResponse && err.status === 403) {
                        this.redirectToSignIn();
                        return;
                    }
                    this.view = 'legacy';
                    this.resolving = false;
                    this.cdr.markForCheck();
                },
            });
    }

    private redirectToSignIn(): void {
        this.authService.signOut().subscribe();
        void this.router.navigate(['/sign-in']);
    }

    private resolveViewMode(body: string): ReportViewMode {
        const trimmed = body.trim();
        if (!trimmed) {
            return 'legacy';
        }

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            return 'legacy';
        }

        if (
            trimmed.startsWith('<!DOCTYPE') ||
            trimmed.startsWith('<!doctype') ||
            trimmed.startsWith('<html') ||
            trimmed.includes('<body')
        ) {
            return 'html';
        }

        return trimmed.includes('<') ? 'html' : 'legacy';
    }
}
